import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useOutletContext } from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale, localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import { buildPageMeta, originFromRequest } from "~/utils/storefrontMeta";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json({ locale: detectLocale(request.url), origin: originFromRequest(request) });
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data?.locale || "vi") as Locale;
  const origin = data?.origin || "https://diolo.com";
  return buildPageMeta({
    title: locale === "vi" ? "Vận chuyển — Diolo" : "Shipping — Diolo",
    description:
      locale === "vi"
        ? "Thông tin vận chuyển Diolo — miễn phí vận chuyển cho đơn hàng trên 1.500.000₫."
        : "Diolo shipping information — free shipping on orders over 1.500.000₫.",
    path: localePath("/shipping", locale),
    origin,
    locale,
  });
};

export default function ShippingPage() {
  const { locale } = useOutletContext<{ locale: Locale }>();

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <Breadcrumbs locale={locale} items={[{ label: t("shipping", locale) }]} />
      {/* Header */}
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {locale === "vi" ? "Thông tin" : "Information"}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          {locale === "vi" ? "Vận chuyển" : "Shipping"}
        </h1>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Free Shipping Banner */}
        <div className="bg-luxury-cream p-6 text-center mb-10 rounded-sm">
          <p className="font-display text-sm uppercase tracking-normal text-luxury-charcoal">
            {locale === "vi"
              ? "Miễn phí vận chuyển cho đơn hàng trên 1.500.000₫"
              : "Complimentary shipping on orders over 1.500.000₫"}
          </p>
        </div>

        {/* Processing */}
        <div className="mb-10">
          <h2 className="font-display text-lg font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Xử lý đơn hàng" : "Order Processing"}
          </h2>
          <p className="text-luxury-stone text-sm leading-relaxed">
            {locale === "vi"
              ? "Đơn hàng đặt trước 14:00 (giờ Việt Nam) trong ngày làm việc sẽ được xử lý trong ngày. Đơn hàng đặt sau 14:00 hoặc vào cuối tuần sẽ được xử lý vào ngày làm việc tiếp theo."
              : "Orders placed before 2:00 PM (Vietnam time) on business days are processed the same day. Orders placed after 2:00 PM or on weekends will be processed on the next business day."}
          </p>
        </div>

        {/* Delivery Options Table */}
        <div className="mb-10">
          <h2 className="font-display text-lg font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Phương thức giao hàng" : "Delivery Options"}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-luxury-sand">
                  <th className="text-left py-3 pr-4 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {locale === "vi" ? "Phương thức" : "Method"}
                  </th>
                  <th className="text-left py-3 pr-4 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {locale === "vi" ? "Thời gian" : "Delivery Time"}
                  </th>
                  <th className="text-left py-3 text-xs uppercase tracking-widest text-luxury-stone font-normal">
                    {locale === "vi" ? "Chi phí" : "Cost"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-luxury-sand/50">
                <tr>
                  <td className="py-4 pr-4 text-luxury-charcoal">
                    {locale === "vi" ? "Giao hàng tiêu chuẩn" : "Standard Delivery"}
                  </td>
                  <td className="py-4 pr-4 text-luxury-stone">
                    {locale === "vi" ? "3–5 ngày làm việc" : "3–5 business days"}
                  </td>
                  <td className="py-4 text-luxury-stone">
                    {locale === "vi" ? "30.000₫ (miễn phí đơn trên 1.500.000₫)" : "30.000₫ (free over 1.500.000₫)"}
                  </td>
                </tr>
                <tr>
                  <td className="py-4 pr-4 text-luxury-charcoal">
                    {locale === "vi" ? "Giao hàng nhanh" : "Express Delivery"}
                  </td>
                  <td className="py-4 pr-4 text-luxury-stone">
                    {locale === "vi" ? "1–2 ngày làm việc" : "1–2 business days"}
                  </td>
                  <td className="py-4 text-luxury-stone">60.000₫</td>
                </tr>
                <tr>
                  <td className="py-4 pr-4 text-luxury-charcoal">
                    {locale === "vi" ? "Giao hàng nội thành HCM" : "Same-City (HCMC)"}
                  </td>
                  <td className="py-4 pr-4 text-luxury-stone">
                    {locale === "vi" ? "Trong ngày" : "Same day"}
                  </td>
                  <td className="py-4 text-luxury-stone">{locale === "vi" ? "45.000₫" : "45,000₫"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Coverage */}
        <div className="mb-10">
          <h2 className="font-display text-lg font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Khu vực giao hàng" : "Delivery Coverage"}
          </h2>
          <p className="text-luxury-stone text-sm leading-relaxed">
            {locale === "vi"
              ? "Chúng tôi giao hàng toàn quốc Việt Nam. Giao hàng quốc tế hiện chưa hỗ trợ nhưng sẽ sớm ra mắt. Vui lòng liên hệ chúng tôi nếu bạn cần giao hàng ra nước ngoài."
              : "We deliver nationwide across Vietnam. International shipping is not yet available but will be launching soon. Please contact us if you require international delivery."}
          </p>
        </div>

        {/* Tracking */}
        <div>
          <h2 className="font-display text-lg font-light text-luxury-charcoal tracking-normal mb-4">
            {locale === "vi" ? "Theo dõi đơn hàng" : "Order Tracking"}
          </h2>
          <p className="text-luxury-stone text-sm leading-relaxed">
            {locale === "vi"
              ? "Sau khi đơn hàng được gửi đi, bạn sẽ nhận được email xác nhận kèm mã vận đơn. Nếu bạn có bất kỳ câu hỏi nào về đơn hàng, vui lòng liên hệ chúng tôi qua email hello@diolo.com."
              : "Once your order ships, you'll receive a confirmation email with tracking information. If you have any questions about your order, please contact us at hello@diolo.com."}
          </p>
        </div>
      </div>
    </div>
  );
}

export { RouteErrorBoundary as ErrorBoundary } from "~/components/RouteErrorBoundary";
